import { useState, useEffect } from "react";
import { getSchools, getStudent, getPupilsRoadmaps } from "../../services/scpApi";
import { yearGroups, typesOfIntervention, studentStatuses, selectableSubjects } from "../../data/data";
import { setSelectedStudent, setSelectedYearGroup as setSelectedYearGroupSlice, setSelectedSchool as setSelectedSchoolSlice } from '../../slices/portalSelectedSlice';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import CustomSelect from "../../features/customSelect/CustomSelect";
import CustomCheckbox from "../../features/customCheckbox/CustomCheckbox";
import CustomMultiSelect from "../../features/customMultiSelect/CustomMultiSelect";
import Report from "./Report";

const Roadmaps = () => {

    const [loading, setLoading] = useState(false);
    const [reportValidationMessage, setReportValidationMessage] = useState('');

    // Schools
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState({});

    // Year groups
    const [selectedYearGroup, setSelectedYearGroup] = useState({})

    // Tutor group leads
    const [tutorGroupLeads, setTutorGroupLeads] = useState([]);
    const [selectedTutorGroupLead, setSelectedTutorGroupLead] = useState({});

    // Tutor groups
    const [tutorGroups, setTutorGroups] = useState([]);
    const [selectedTutorGroup, setSelectedTutorGroup] = useState({});

    // Intervention types
    const [selectedInterventionTypes, setSelectedInterventionTypes] = useState([]);

    // Student statuses multi-select
    const [selectedStudentStatuses, setSelectedStudentStatuses] = useState([]);

    // Include low absenteeism
    const [includeNormalAttendance, setIncludeNormalAttendance] = useState(true);

    // Include medium & high absenteeism
    const [includeLowAttendance, setIncludeLowAttendance] = useState(false);

    // Roadmaps
    const [roadmaps, setRoadmaps] = useState([]);

    const navigate = useNavigate();
    const dispatch = useDispatch();


    // get schools
    useEffect(() => {
        (async () => {
            try {
                const response = await getSchools();

                // JSON Parsing the models filter_data 
                Array.isArray(response) && response.length > 0 && response.map(school => {
                    school.filter_data = JSON.parse(school.filter_data);
                })

                setSchools(response);

            } catch (error) {
                console.error(error);
            }
        })();
    }, [])

    // Get tutor group leads on selectedSchool Change
    useEffect(() => {
        if (!selectedSchool && !selectedSchool.id && !selectedSchool?.filter_data?.tutors) return;
        resetAllFields();

        setTutorGroupLeads(selectedSchool?.filter_data?.tutors || []);

        // Reset dependent fields
        resetAllFields();

    }, [selectedSchool]);

    // Set tutor groups on selectedTutorGroupLead change
    useEffect(() => {
        if (!selectedTutorGroupLead || !selectedTutorGroupLead.id) return;

        if (selectedTutorGroupLead?.tutor_groups && Array.isArray(selectedTutorGroupLead.tutor_groups)) {
            selectedTutorGroupLead.tutor_groups.map(group => group.name = `${selectedTutorGroupLead.name} - ${group.name}`);
        }

        setTutorGroups(selectedTutorGroupLead?.tutor_groups || []);

    }, [selectedTutorGroupLead]);


    // Multi select intervention types
    const toggleInterventionTypeSelection = (type) => {
        if (!type) {
            setSelectedInterventionTypes([]);
        }

        setSelectedInterventionTypes((prevTypes) => {
            if (prevTypes.find((t) => t.type === type.type)) {
                // remove type from selected types
                return prevTypes.filter((t) => t.type !== type.type);
            } else {
                // add type to selected types
                return [...prevTypes, type];
            }
        });
    }


    // Multi select student status
    const toggleStudentStatusSelection = (status) => {
        if (!status) {
            setSelectedStudentStatuses([]);
        }

        setSelectedStudentStatuses((prevStatuses) => {
            if (prevStatuses.find((s) => s.status === status.status)) {
                // remove status from selected statuses
                return prevStatuses.filter((s) => s.status !== status.status);
            } else {
                // add status to selected statuses
                return [...prevStatuses, status];
            }
        });
    }


    // Generate report 
    const generateReport = () => {
        if (!selectedSchool.id) {
            return;
        }

        // setReportGenerated(false);
        setLoading(true);

        //reset validation message
        setReportValidationMessage("");

        // prepare fetch args
        const args = {
            schoolId: selectedSchool.id,
        };

        // year group
        if (selectedYearGroup.year) {
            args.yearGroup = selectedYearGroup.year;
        }

        // all tutor's tutor groups if not selected and tutor selected
        if (selectedTutorGroupLead.id && !selectedTutorGroup.id) {
            args.tutorGroupId = Array.from(tutorGroups.map((group) => group.id));
        }

        // selected tutor group if is selected
        if (selectedTutorGroup.id) {
            args.tutorGroupId = [selectedTutorGroup.id];
        }

        // fetch
        (async () => {
            try {
                const response = await getPupilsRoadmaps(args);

                if (!response || !response?.roadmaps) {
                    console.log('No students found for the selected filters');
                    setLoading(false);
                    setRoadmaps([]);
                }

                // Perform aditional client side filtering
                const filteredResponse = clientSideFiltering(response?.roadmaps);

                // Sort the roadmaps based on students first names
                filteredResponse.sort((a, b) => {
                    const nameA = a?.pupil_info?.name?.toLowerCase() || '';
                    const nameB = b?.pupil_info?.name?.toLowerCase() || '';
                    return nameA.localeCompare(nameB);
                });

                if (filteredResponse.length === 0) {
                    setReportValidationMessage("No students found for the selected filters");
                }

                setRoadmaps(filteredResponse);

            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        })();
    }

    // Additional client side filtering 
    const clientSideFiltering = (responseData) => {

        if (selectedInterventionTypes.length > 0) {
            const allowedInterventionTypes = selectedInterventionTypes.map(type => type.type);
            responseData = responseData.filter(roadmap => allowedInterventionTypes.includes(roadmap?.pupil_info?.intervention_type));
        }

        if (selectedStudentStatuses.length > 0) {
            const allowedStudentStatuses = selectedStudentStatuses.map(status => status.status);
            responseData = responseData.filter(roadmap => allowedStudentStatuses.includes(roadmap?.pupil_info?.pupil_status));
        }

        if (!includeNormalAttendance) {
            responseData = responseData.filter(roadmap => roadmap?.pupil_info?.attendance !== 'normal');
        }

        if (!includeLowAttendance) {
            responseData = responseData.filter(roadmap => roadmap?.pupil_info?.attendance !== 'low');
        }

        return responseData
    }

    // Handle student click in the report to set fetch the student data, set it in the global state and navigate to the update student progress page
    const handleStudentClick = async (studentId) => {
        if (!studentId) return;

        const student = await getStudent(studentId);
        const studentSchool = student?.school || {};
        const studentYearGroup = student?.pupil_periods?.length > 0 ? student.pupil_periods[student.pupil_periods.length - 1]?.year_group : {};
        const studentYearGroupObj = {
            name: `Year ${studentYearGroup}`,
            year: studentYearGroup,
        }

        if (student) {    
            dispatch(setSelectedStudent(student));
            dispatch(setSelectedSchoolSlice(studentSchool));
            dispatch(setSelectedYearGroupSlice(studentYearGroupObj));

            navigate('/portal/students/update-student-progress');
        }

    }


    const resetAllFields = () => {
        setSelectedYearGroup({});
        setSelectedTutorGroupLead({});
        setSelectedTutorGroup({});
        setRoadmaps([]);
    }


    const clearFilters = () => {
        setSelectedSchool({});
        setSelectedYearGroup({});
        setSelectedTutorGroupLead({});
        setSelectedTutorGroup({});
        setSelectedInterventionTypes([]);
        setSelectedStudentStatuses([]);
        setIncludeNormalAttendance(true);
        setIncludeLowAttendance(false);
        setReportValidationMessage("");
        setLoading(false);
        setRoadmaps([]);
    }

    // useEffect to reset roadmaps if filters change
    useEffect(() => {
        setRoadmaps([]);
        setReportValidationMessage("");
    }, [
        selectedSchool,
        selectedYearGroup,
        selectedTutorGroupLead,
        selectedTutorGroup,
        selectedInterventionTypes,
        selectedStudentStatuses,
        includeNormalAttendance,
        includeLowAttendance
    ]);

    return (
        <div className="roadmaps-report">
            <h1>Roadmaps report</h1>

            <div className="full-width-select-cont">
                <CustomSelect
                    name="select-school"
                    placeholder="Select School"
                    options={schools}
                    labelKey="name"
                    setSelectedOption={(option) => setSelectedSchool(option)}
                    selectedOption={selectedSchool}
                />
            </div>


            <div className="separator"></div>

            <div className="select-report-filters-cont">

                <h3>Select Report Filters</h3>

                <div className="flex-row-select-cont">
                    <CustomSelect
                        name="select-year-group"
                        placeholder="Select Year Group"
                        options={yearGroups}
                        labelKey="name"
                        setSelectedOption={(option) => setSelectedYearGroup(option)}
                        selectedOption={selectedYearGroup}
                    />

                    <CustomSelect
                        name="select-tutor-group-lead"
                        placeholder="Select tutor group lead"
                        options={tutorGroupLeads}
                        labelKey="name"
                        setSelectedOption={(option) => {
                            setSelectedTutorGroupLead(option);
                            // reset dependent tutor group
                            setSelectedTutorGroup({});
                        }}
                        selectedOption={selectedTutorGroupLead}
                    />

                    <CustomSelect
                        name="select-tutor-group"
                        placeholder="Select tutor group"
                        options={tutorGroups}
                        labelKey="name"
                        setSelectedOption={(option) => setSelectedTutorGroup(option)}
                        selectedOption={selectedTutorGroup}
                    />
                </div>

                <div className="flex-row-select-cont">
                    <CustomMultiSelect
                        name="select-intervention-type"
                        placeholder="Select intervention type"
                        options={typesOfIntervention}
                        labelKey="name"
                        setSelectedOption={(option) => toggleInterventionTypeSelection(option)}
                        selectedOptions={selectedInterventionTypes}
                    />
                </div>

                <div className="flex-row-select-cont">
                    <CustomMultiSelect
                        name="select-student-status"
                        placeholder="Select student status"
                        options={studentStatuses}
                        labelKey="name"
                        setSelectedOption={(option) => toggleStudentStatusSelection(option)}
                        selectedOptions={selectedStudentStatuses}
                    />
                </div>

                <div className="flex-row-checkbox-cont">
                    <CustomCheckbox
                        name="checkbox-normal-attendance"
                        label="Include normal attendance marked students"
                        checked={includeNormalAttendance}
                        setChecked={(newStatus) => setIncludeNormalAttendance(newStatus)}
                    />

                    <CustomCheckbox
                        name="checkbox-include-low-attendance"
                        label="Include low attendance marked students"
                        checked={includeLowAttendance}
                        setChecked={(newStatus) => setIncludeLowAttendance(newStatus)}
                    />
                </div>

                <a onClick={() => clearFilters()} className="clear-filters-btn">Clear Filters</a>
                <button onClick={() => generateReport()} disabled={loading}>{loading ? 'Loading... ' : 'Generate Report'}</button>
                <p className="generate-report-validation-msg">{reportValidationMessage}</p>

            </div>

            <div className="separator"></div>

            {/* If there are roadmaps, display the Report component */}
            {roadmaps && roadmaps.length > 0 && selectedSchool &&
                <Report
                    reportData={roadmaps}
                    selectedSchool={selectedSchool}
                    selectedTutorGroupLead={selectedTutorGroupLead}
                    selectedTutorGroup={selectedTutorGroup}
                    selectedYearGroup={selectedYearGroup}
                    selectedStudentStatuses={selectedStudentStatuses}
                    selectedInterventionTypes={selectedInterventionTypes}
                    includeNormalAttendance={includeNormalAttendance}
                    includeLowAttendance={includeLowAttendance}
                    handleStudentClick={handleStudentClick} />
            }


        </div>
    );
}
export default Roadmaps;